import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Search as SearchIcon } from "lucide-react";

export default function MultiSelectDropdown({
  icon: Icon,
  iconClassName = "text-slate-400",
  values = [],
  onChange,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  triggerClassName = "",
  showSearch = true,
  "data-testid": dataTestId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const focusTimer = showSearch ? setTimeout(() => inputRef.current?.focus(), 10) : null;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      if (focusTimer) clearTimeout(focusTimer);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, showSearch]);

  const q = query.trim().toLowerCase();
  const filtered = (q
    ? options.filter(o => o.label.toLowerCase().includes(q))
    : options
  ).slice().sort((a, b) => a.label.localeCompare(b.label));

  const toggleValue = (v) => {
    onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v]);
  };

  const label = values.length === 0
    ? placeholder
    : values.length === 1
      ? (options.find(o => o.value === values[0])?.label || values[0])
      : `${values.length} selected`;

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={dataTestId}
        className={`flex items-center gap-2 w-full text-left ${triggerClassName}`}
      >
        {Icon && <Icon className={`w-4 h-4 shrink-0 ${iconClassName}`} />}
        <span className={`truncate flex-1 text-sm ${values.length ? "text-slate-800" : "text-slate-500"}`}>
          {label}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[240px] bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {showSearch && (
            <div className="p-2 border-b border-slate-100 relative">
              <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black pointer-events-none" />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-7 pr-2 py-1.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-1 focus:ring-black"
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-slate-400">No matches</p>
            ) : filtered.map(o => (
              <label
                key={o.value}
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={values.includes(o.value)}
                  onChange={() => toggleValue(o.value)}
                  style={{ accentColor: "#000" }}
                  className="w-4 h-4 rounded border-slate-300 shrink-0"
                />
                <span className="truncate flex-1 text-slate-700">{o.label}</span>
                {o.count > 0 && <span className="ml-auto pl-2 text-xs text-slate-400 shrink-0">{o.count}</span>}
              </label>
            ))}
          </div>
          {values.length > 0 && (
            <div className="border-t border-slate-100 p-2">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-slate-500 hover:text-black underline"
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
